import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { SOURCES, getImageUrl } from 'client/utils/image-helpers';
import { getPhotoflipperProps, OVERLAY_TYPE, PHOTO_ROW_THRESHOLD } from 'site-modules/core-page/utils/photo-gallery';
import { EDMUNDS_CONTENT } from 'site-modules/shared/utils/core-page/core-media';
import { CorePageParams } from 'site-modules/shared/utils/core-page/params';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';
import { PhotoflipperButtonAsync } from 'site-modules/shared/components/photoflipper-core/photoflipper-button/photoflipper-button-async';
import { TextOverlay } from './overlays/text-overlay';

export function PhotoGalleryPhoto({
  photoData,
  params,
  media = null,
  isPreprod = false,
  overlayType = null,
  totalCount = 0,
  children = null,
  index,
  isHeroPhoto = false,
  isMobile = false,
  isCore6981Test = false,
}) {
  const { category, num, title, src, isLifestylePhoto, author, provider } = photoData;
  const imgUrl = getImageUrl(src, undefined, isHeroPhoto && params.isUsed ? SOURCES.EDMUNDSCDN : SOURCES.MEDIA);
  const photoflipperProps = getPhotoflipperProps({
    photoNum: num,
    params,
    altMedia: media,
    category,
    isPreprod,
    isCore6981Test,
  });

  const isAriaHidden = !isHeroPhoto && !isMobile;
  // -1 to exclude main photo as well
  const restCount = totalCount - PHOTO_ROW_THRESHOLD - 1;
  const photoTrackingId = isHeroPhoto ? 'open_photoflipper_hero' : 'open_photoflipper_thumbnail';
  return (
    <div className="pos-r">
      <PhotoflipperButtonAsync tag={Fragment} {...photoflipperProps} dataTrackingId={photoTrackingId}>
        <AspectRatioContainer
          aspectRatio={isHeroPhoto ? undefined : '15:9'}
          role="button"
          className="photo-gallery-photo rounded-12"
          aria-hidden={isAriaHidden}
          tabIndex={isAriaHidden ? -1 : 0}
          data-tracking-value={index}
          data-tracking-parent="photoflipper"
        >
          <img
            className="photo w-100"
            alt={title}
            title={title}
            src={imgUrl}
            data-lifestyle-photo={isLifestylePhoto}
            fetchPriority={isHeroPhoto ? 'high' : 'low'}
            loading={isHeroPhoto ? 'eager' : 'lazy'}
          />
          {provider === EDMUNDS_CONTENT && !!author && (
            <div
              className="small text-cool-gray-10 pos-a"
              style={{
                transform: 'none',
                width: 'auto',
                top: 'auto',
                left: 0,
                bottom: 0,
                padding: '0.25rem',
                backgroundColor: 'rgba(255, 255, 255, 0.65)',
              }}
            >
              <span className="visually-hidden">photo by </span>
              {author}
            </div>
          )}
          {children}
          <span className="visually-hidden">View {totalCount > 2 ? totalCount - 1 : ''} more photos</span>
        </AspectRatioContainer>
      </PhotoflipperButtonAsync>
      {overlayType === OVERLAY_TYPE.REST_PHOTOS && restCount > 0 && <TextOverlay text={`+${restCount}`} />}
    </div>
  );
}

PhotoGalleryPhoto.propTypes = {
  params: CorePageParams.isRequired,
  photoData: PropTypes.shape({}).isRequired,
  media: PropTypes.shape({
    photos: PropTypes.arrayOf(PropTypes.shape({})),
    videos: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  totalCount: PropTypes.number,
  index: PropTypes.number,
  overlayType: PropTypes.oneOf(Object.values(OVERLAY_TYPE)),
  children: PropTypes.node,
  isPreprod: PropTypes.bool,
  isHeroPhoto: PropTypes.bool,
  isMobile: PropTypes.bool,
  isCore6981Test: PropTypes.bool,
};
